import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { X, Mail, Lock, Smartphone, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

// Emergent-managed Google login: redirect out, then /auth/callback exchanges the session.
const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginModal({ open, onClose, onSignupClick }) {
  const navigate = useNavigate();
  const { login, setSession } = useAuth();

  const [mode, setMode] = useState('email'); // 'email' | 'mobile'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('email'); setEmail(''); setPassword(''); setPhone(''); setOtp(''); setOtpSent(false); setLoading(false);
  }, [open]);

  if (!open) return null;

  const goByRole = (u) => {
    if (u.role === 'driver') navigate('/driver');
    else if (u.role === 'merchant_admin' || u.role === 'merchant_user') navigate('/merchant');
    else navigate('/dashboard');
  };

  const finish = (data) => {
    setSession(data.token, data.user);
    toast.success('Welcome back!');
    onClose();
    goByRole(data.user);
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success('Welcome back!');
      onClose();
      goByRole(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/otp/send`, { phone_number: phone });
      setOtpSent(true);
      toast.success('Verification code sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/otp/verify`, { phone_number: phone, code: otp });
      finish(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="login-modal"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          data-testid="login-modal-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">Welcome back</h3>
        <p className="mt-1.5 text-sm text-slate-500">Log in to your Takeitto account</p>

        {/* Google (Emergent-managed) */}
        <div className="mt-6">
          <button
            type="button"
            onClick={startGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid="login-google-btn"
          >
            <GoogleIcon />
            Continue with Google
          </button>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-3 text-xs uppercase tracking-wider text-slate-400">or</span></div>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
          <button
            onClick={() => setMode('email')}
            className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === 'email' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
            data-testid="login-tab-email"
          >
            <Mail size={15} /> Email
          </button>
          <button
            onClick={() => setMode('mobile')}
            className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === 'mobile' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
            data-testid="login-tab-mobile"
          >
            <Smartphone size={15} /> Mobile
          </button>
        </div>

        {/* Email form */}
        {mode === 'email' && (
          <form onSubmit={handleEmail} className="mt-5 space-y-3" data-testid="login-email-form">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-indigo-400 text-sm"
                data-testid="login-email-input"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-indigo-400 text-sm"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60"
              data-testid="login-email-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : 'Log in'}
            </button>
          </form>
        )}

        {/* Mobile OTP form */}
        {mode === 'mobile' && (
          <div className="mt-5 space-y-3" data-testid="login-mobile-form">
            {!otpSent ? (
              <form onSubmit={sendOtp} className="space-y-3">
                <div className="relative">
                  <Smartphone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="+61 400 000 000"
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-indigo-400 text-sm"
                    data-testid="login-phone-input"
                  />
                </div>
                <p className="text-xs text-slate-400">We'll text you a one-time code. Include your country code.</p>
                <button
                  type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  data-testid="login-send-otp-btn"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : 'Send code'}
                </button>
              </form>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-3">
                <button type="button" onClick={() => { setOtpSent(false); setOtp(''); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="login-otp-back">
                  <ArrowLeft size={15} /> {phone}
                </button>
                <input
                  type="text" inputMode="numeric" required value={otp} onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className="w-full px-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-indigo-400 text-sm tracking-[0.3em] text-center font-semibold"
                  data-testid="login-otp-input"
                />
                <button
                  type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  data-testid="login-verify-otp-btn"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : 'Verify & log in'}
                </button>
              </form>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-sm text-slate-500">
          Don't have an account?{' '}
          <button
            onClick={() => { onClose(); onSignupClick && onSignupClick(); }}
            className="font-semibold text-indigo-600 hover:underline"
            data-testid="login-modal-signup-link"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
}
